/**
 * תוכן סטטי לעמוד הרשמת סוכן — חבילות 2026 (BASIC / PRO / סוכנות).
 * מזהי חבילה ל־form state נשארים BASIC / PRO / AGENCY (כמו package_id מחרוזתי בקטלוג).
 */

import {
  formatPackageSubscriptionGrossPrice,
  resolvePackagePriceWithVat,
} from "./resolvePackagePriceWithVat";

export const REGISTRATION_PACKAGE_CODES = {
  TRIAL: "TRIAL",
  BASIC: "BASIC",
  PRO: "PRO",
  AGENCY: "AGENCY",
};

/** ניסיון הרשמה ללא חיוב וללא כרטיס אשראי. */
export const AGENT_TRIAL_DAYS = 60;

/** סכומי נטו בקטלוג — לתצוגה/תשלום יש להשתמש ב-resolvePackagePriceWithVat().grossAmount בלבד. */
export const REGISTRATION_PACKAGE_NET_AMOUNTS = {
  [REGISTRATION_PACKAGE_CODES.TRIAL]: 0,
  [REGISTRATION_PACKAGE_CODES.BASIC]: 399,
  [REGISTRATION_PACKAGE_CODES.PRO]: 599,
  [REGISTRATION_PACKAGE_CODES.AGENCY]: 1000,
};

const VAT_INCLUDED_LINE = 'מחירים כוללים מע"מ';

/** סדר תצוגה */
export const REGISTRATION_PLAN_ORDER = [
  REGISTRATION_PACKAGE_CODES.TRIAL,
  REGISTRATION_PACKAGE_CODES.BASIC,
  REGISTRATION_PACKAGE_CODES.PRO,
  REGISTRATION_PACKAGE_CODES.AGENCY,
];

function buildPlanDisplayFields(monthlyNetAmount, billingExtras = {}) {
  if (Number(monthlyNetAmount) === 0 && billingExtras.freePriceMain) {
    return {
      priceMain: billingExtras.freePriceMain,
      priceVatLine: billingExtras.freePriceMeta || "",
      billing: {
        displayName: billingExtras.displayName,
        monthlyPriceLabel: billingExtras.freePriceMain,
      },
    };
  }
  const grossLabel = formatPackageSubscriptionGrossPrice(monthlyNetAmount, {
    suffix: billingExtras.priceSuffix ?? "לחודש",
  });
  return {
    priceMain: grossLabel,
    priceVatLine: VAT_INCLUDED_LINE,
    billing: {
      displayName: billingExtras.displayName,
      monthlyPriceLabel: grossLabel,
    },
  };
}

const PLANS = {
  [REGISTRATION_PACKAGE_CODES.TRIAL]: {
    code: REGISTRATION_PACKAGE_CODES.TRIAL,
    name: "ניסיון 60 יום",
    subtitle: "להתחיל בלי עלות ובלי כרטיס אשראי",
    accent: "#0369a1",
    iconType: "GiftOutlined",
    badge: "ללא כרטיס",
    monthlyNetAmount: REGISTRATION_PACKAGE_NET_AMOUNTS.TRIAL,
    requiresPaymentMethod: false,
    trialDays: AGENT_TRIAL_DAYS,
    noCommitment: "60 יום ללא עלות · ללא כרטיס אשראי",
    ctaLabel: "התחילו 60 יום ניסיון",
    features: [
      "פתיחת חשבון ללא כרטיס אשראי",
      "60 יום ניסיון ללא עלות",
      "מערכת חבילות נופש ומלונות",
      "סליקה ופיצול תשלומים",
      "בתום הניסיון בוחרים חבילה בתשלום",
    ],
    extraSections: [
      {
        title: "אחרי 60 יום",
        lines: [
          "אין חיוב אוטומטי. כדי להמשיך בוחרים BASIC, PRO או סוכנות ושומרים אמצעי תשלום.",
        ],
      },
    ],
    billing: {
      displayName: "ניסיון 60 יום",
      freePriceMain: "ללא עלות",
      freePriceMeta: "60 יום · ללא כרטיס אשראי",
    },
  },
  [REGISTRATION_PACKAGE_CODES.BASIC]: {
    code: REGISTRATION_PACKAGE_CODES.BASIC,
    name: "סוכן עצמאי BASIC",
    subtitle: "להתחיל למכור בלי סיכון",
    accent: "#0d9488",
    iconType: "UserOutlined",
    badge: null,
    monthlyNetAmount: REGISTRATION_PACKAGE_NET_AMOUNTS.BASIC,
    noCommitment: "ללא התחייבות",
    ctaLabel: "התחל עם BASIC",
    features: [
      "מערכת חבילות נופש ומלונות",
      "מנו ספנות – 5% עמלה",
      "סליקה ופיצול תשלומים",
      "אטרקציות והרחבות",
      "גישה להדרכות אונליין",
      "תמיכה במייל",
      "עבודה במחירי נטו מול ספקים",
    ],
    extraSections: [
      {
        title: "הטבת ביצועים",
        lines: [
          "מעל 60,000 ₪ מחזור חודשי → פטור מדמי מנוי לחודש העוקב",
        ],
      },
    ],
    billing: {
      displayName: "סוכן עצמאי BASIC",
    },
  },
  [REGISTRATION_PACKAGE_CODES.PRO]: {
    code: REGISTRATION_PACKAGE_CODES.PRO,
    name: "סוכן עצמאי PRO",
    subtitle: "להרוויח יותר מכל עסקה",
    accent: "#c2410c",
    iconType: "CrownOutlined",
    badge: "הבחירה המומלצת",
    monthlyNetAmount: REGISTRATION_PACKAGE_NET_AMOUNTS.PRO,
    noCommitment: "ללא התחייבות",
    ctaLabel: "הצטרף ל-PRO",
    features: [
      "כל מה שב-BASIC",
      "מערכת לידים",
      "אתר סוכן עם יכולות AI",
      "מערכת SEO להבאת תנועה",
      "כלים מתקדמים למכירה",
      "הצעות מחיר חכמות",
      "עדיפות בתמיכה (טלפון ומייל)",
      "גישה למערכת Crazy Llama",
      "מנו ספנות – 10% עמלה",
      "טיסות במערכת (נכון להיום – צ'רטר)",
      "טיולים מאורגנים",
      "אפשרות לשריין מלונות מראש",
      "גישה להדרכות אונליין",
    ],
    extraSections: [
      {
        title: "הטבת ביצועים",
        lines: [
          "מעל 50,000 ₪ מחזור חודשי → פטור מדמי מנוי לחודש העוקב",
        ],
      },
    ],
    billing: {
      displayName: "סוכן עצמאי PRO",
    },
  },
  [REGISTRATION_PACKAGE_CODES.AGENCY]: {
    code: REGISTRATION_PACKAGE_CODES.AGENCY,
    name: "סוכנות",
    subtitle: "לבנות צוות ולגדול",
    accent: "#1d4ed8",
    iconType: "BankOutlined",
    badge: null,
    monthlyNetAmount: REGISTRATION_PACKAGE_NET_AMOUNTS.AGENCY,
    noCommitment: "ללא התחייבות · עד 4 משתמשים כלולים",
    ctaLabel: "פתח חשבון סוכנות",
    features: [
      "כל מה שב-PRO",
      "ניהול צוות סוכנים והרשאות",
      "שליטה מלאה בתמחור ורווחיות",
      "עבודה במחירי נטו",
    ],
    extraSections: [],
    billing: {
      displayName: "סוכנות",
      priceSuffix: "לחודש (עד 4 משתמשים)",
    },
  },
};

function enrichPlan(plan) {
  const display = buildPlanDisplayFields(plan.monthlyNetAmount, plan.billing);
  return {
    ...plan,
    ...display,
    billing: display.billing,
  };
}

export function listRegistrationPlansOrdered() {
  return REGISTRATION_PLAN_ORDER.map((code) => enrichPlan(PLANS[code]));
}

export function getRegistrationPlanDefinition(code) {
  const raw = code == null ? "" : String(code).trim().toUpperCase();
  const normalized = raw === "STARTER" ? REGISTRATION_PACKAGE_CODES.BASIC : raw;
  const plan = PLANS[normalized];
  return plan ? enrichPlan(plan) : null;
}

export function registrationPlanRequiresPaymentMethod(code) {
  const plan = getRegistrationPlanDefinition(code);
  if (!plan) return true;
  return plan.requiresPaymentMethod !== false;
}

export function getRegistrationPlanBillingSummary(code) {
  const plan = getRegistrationPlanDefinition(code);
  if (!plan) {
    return {
      name: String(code || "חבילה"),
      monthlyPriceLabel: "—",
    };
  }
  return {
    name: plan.billing.displayName,
    monthlyPriceLabel: plan.billing.monthlyPriceLabel,
  };
}
